import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

/* ------------------------------------------------------------------------
   Primitives ported from forge/design/components. Sans inside every box,
   serif only unboxed, one accent, sentences 16px and up, 44px tap targets.
   ------------------------------------------------------------------------ */

const cx = (...parts) => parts.filter(Boolean).join(' ');

export const SITE = 'Right Brain Labs';
export const TAGLINE = 'The agency to think with AI, not follow it.';
export const ONE_LINER = 'Right Brain Labs helps humans and organizations build the cognitive infrastructure to think with AI, not follow it.';
export const CONTACT_MAILTO = 'mailto:agent@rightbrainlabs.ai?subject=Start%20a%20conversation';
export const SUBSTACK_URL = 'https://rightbrainedai.substack.com';
export const LINKEDIN_URL = 'https://www.linkedin.com/company/right-brain-labs-ai/';

/** Per-page head. Title, description, canonical, Open Graph. */
export const PageHead = ({ title, description, path = '/', image = '/og-default.png', jsonLd }) => {
  const fullTitle = title ? `${title} | ${SITE}` : `${SITE}: ${TAGLINE}`;
  const url = `https://www.rightbrainlabs.ai${path}`;
  const img = image.startsWith('http') ? image : `https://www.rightbrainlabs.ai${image}`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

/** Page-width container with the system gutters. */
export const Container = ({ children, className = '', wide = false }) => (
  <div className={cx('mx-auto w-full px-5 md:px-8', wide ? 'max-w-[1440px]' : 'max-w-page', className)}>
    {children}
  </div>
);

/**
 * Section band. tone: 'page' (default light ground), 'card' (white),
 * 'dark' (the one navy panel). Vertical rhythm from the tokens.
 */
export const Section = ({ children, tone = 'page', className = '', id, reveal = true }) => {
  // className may carry !important padding overrides for tight bands
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || !reveal) return undefined;
    if (typeof IntersectionObserver === 'undefined' || /ReactSnap|HeadlessChrome/i.test(navigator.userAgent)) {
      el.classList.add('is-visible');
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [reveal]);
  const tones = {
    page: 'bg-navy-50 text-navy-600 dark:bg-navy-900 dark:text-[#E8ECF3]',
    card: 'bg-white text-navy-600 dark:bg-navy-850 dark:text-[#E8ECF3]',
    dark: 'bg-navy-900 text-[#E8ECF3]',
  };
  return (
    <section id={id} ref={ref} className={cx(tones[tone], 'py-section md:py-section-lg', reveal && 'reveal', className)}>
      <Container>{children}</Container>
    </section>
  );
};

/** All-caps eyebrow label. Sans, 14px, 0.16em tracking (tokens). */
export const Eyebrow = ({ children, onDark = false, className = '' }) => (
  <div className={cx('font-sans text-eyebrow uppercase font-semibold mb-3',
    onDark ? 'text-emerald-300' : 'text-emerald-500', className)}>
    {children}
  </div>
);

/** Section head. Serif, unboxed. */
export const SectionHead = ({ children, onDark = false, className = '', as: Tag = 'h2' }) => (
  <Tag className={cx('font-display font-bold text-section-head tracking-tight',
    onDark ? 'text-white' : 'text-navy-800 dark:text-white', className)}>
    {children}
  </Tag>
);

/** Page title. Serif, largest size. */
export const PageTitle = ({ children, onDark = false, className = '' }) => (
  <h1 className={cx('font-display font-bold text-page-title tracking-tight',
    onDark ? 'text-white' : 'text-navy-800 dark:text-white', className)}>
    {children}
  </h1>
);

/** Lede under a title. Serif, 60ch measure. */
export const Lede = ({ children, onDark = false, className = '' }) => (
  <p className={cx('font-display text-lede max-w-[84ch] mt-5',
    onDark ? 'text-[#C9D2E0]' : 'text-navy-600 dark:text-[#C9D2E0]', className)}>
    {children}
  </p>
);

/**
 * Button. variant: primary (emerald), secondary (outline), ghost (text).
 * Renders a router Link for internal paths, an anchor for external ones,
 * or a button when onClick is given without href.
 */
export const Button = ({ href, to, onClick, children, variant = 'primary', onDark = false, className = '', ...rest }) => {
  const base = 'inline-flex items-center justify-center gap-2 min-h-tap px-5 rounded-sm font-sans text-button font-semibold leading-none transition-colors whitespace-nowrap';
  const variants = {
    primary: 'bg-emerald-500 text-white hover:bg-emerald-600 border border-emerald-500 hover:border-emerald-600',
    secondary: onDark
      ? 'bg-transparent text-white border border-[#4E5B78] hover:border-white'
      : 'bg-transparent text-navy-800 border border-navy-300 hover:border-navy-800 dark:text-white dark:border-navy-700 dark:hover:border-white',
    ghost: onDark
      ? 'bg-transparent text-emerald-300 hover:text-white px-2'
      : 'bg-transparent text-emerald-500 hover:text-emerald-600 px-2',
  };
  const cls = cx(base, variants[variant], className);
  if (to) return <Link to={to} className={cls} {...rest}>{children}</Link>;
  if (href) {
    const external = /^https?:|^mailto:/.test(href);
    return <a href={href} className={cls} {...(external && href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})} {...rest}>{children}</a>;
  }
  return <button type="button" onClick={onClick} className={cls} {...rest}>{children}</button>;
};

/** Card. Everything inside is sans. Optional accent edge on top. */
export const Card = ({ children, accent = false, className = '', onDark = false, as: Tag = 'div' }) => (
  <Tag className={cx('rounded-lg border p-5 md:p-[26px]',
    onDark ? 'bg-navy-850 border-navy-700 text-[#E8ECF3]' : 'bg-white border-navy-200 text-navy-600 dark:bg-navy-850 dark:border-navy-700 dark:text-[#E8ECF3]',
    accent && 'border-t-4 border-t-emerald-500',
    className)}>
    {children}
  </Tag>
);

export const CardTitle = ({ children, className = '' }) => (
  <h3 className={cx('font-sans font-semibold text-card-title text-navy-800 dark:text-white', className)}>{children}</h3>
);

export const CardBody = ({ children, className = '' }) => (
  <p className={cx('font-sans text-interface mt-2 text-navy-600 dark:text-[#C9D2E0]', className)}>{children}</p>
);

/** Stat card. Sans numeral, the one sans size above 24px. */
export const StatCard = ({ value, suffix = '', label, note, countRef, className = '' }) => (
  <div className={cx('rounded-lg border border-navy-200 bg-white p-5 dark:bg-navy-850 dark:border-navy-700', className)}>
    <div ref={countRef} className="font-sans font-semibold text-stat-lg text-navy-800 dark:text-white tabular-nums">
      {value}{suffix}
    </div>
    <div className="font-sans text-interface font-medium mt-2 text-navy-800 dark:text-white">{label}</div>
    {note && <div className="font-sans text-small mt-1 text-navy-500 dark:text-navy-400">{note}</div>}
  </div>
);

/** Pull quote. Serif, unboxed, 52ch. */
export const PullQuote = ({ children, attribution, onDark = false, className = '' }) => (
  <figure className={cx('max-w-[52ch] mx-auto text-center', className)}>
    <blockquote className={cx('font-display font-semibold text-quote', onDark ? 'text-white' : 'text-navy-800 dark:text-white')}>
      {children}
    </blockquote>
    {attribution && (
      <figcaption className={cx('font-sans text-interface mt-4', onDark ? 'text-navy-400' : 'text-navy-500 dark:text-navy-400')}>
        {attribution}
      </figcaption>
    )}
  </figure>
);


/**
 * Saying. One of Srini's lines, set as a block quote per the aphorisms
 * canon: serif italic, an emerald rule, attribution beneath. One per
 * section at most, never two in a row. Wording is never changed.
 */
export const Saying = ({ children, tag, className = '' }) => (
  <figure className={cx('border-l-4 border-emerald-500 pl-6 md:pl-8 py-1 max-w-[80ch]', className)}>
    <blockquote className="font-display italic font-semibold text-quote text-emerald-700 dark:text-emerald-300">
      {children}
    </blockquote>
    <figcaption className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="font-sans text-label uppercase font-semibold text-navy-800 dark:text-white">Srini Koushik</span>
      {tag && <span className="font-sans text-label uppercase text-navy-500 dark:text-navy-400">{tag}</span>}
    </figcaption>
  </figure>
);

/** Tag / pill. */
export const Tag = ({ children, className = '' }) => (
  <span className={cx('inline-flex items-center rounded-full bg-emerald-50 text-emerald-600 font-sans text-tag font-medium px-3 py-1 dark:bg-navy-800 dark:text-emerald-300', className)}>
    {children}
  </span>
);

/** The one dark panel: the closing call to action on every page. */
export const ConversationPanel = ({
  head = 'Bring the decision you are stuck on.',
  body = 'One conversation, no deck. If we are not the right people for it, we will say so.',
}) => (
  <Section tone="dark" reveal={false}>
    <div className="max-w-[68ch]">
      <Eyebrow onDark>Start a conversation</Eyebrow>
      <SectionHead onDark>{head}</SectionHead>
      <p className="font-display text-lede mt-4 text-[#C9D2E0]">{body}</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Button href={CONTACT_MAILTO}>Start a conversation</Button>
        <Button to="/compass" variant="secondary" onDark>Take the Compass</Button>
      </div>
    </div>
  </Section>
);

/** Two-column grid that collapses on the phone. */
export const Grid = ({ children, cols = 3, className = '' }) => {
  const map = { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-2 lg:grid-cols-4' };
  return <div className={cx('grid grid-cols-1 gap-[14px]', map[cols], className)}>{children}</div>;
};
